import {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState, store} from '../../redux/store';
import {setUniqueId, login} from '../../screens/auth/login/reducer';
import {getLocalData, setLocalData} from '../storage';
import {keys} from '../../api/keys';
import {isWeb} from '../responsive';
import http from '../../api/http';
import {setSelectedCase} from '../../screens/content/chat/reducer';
import {services} from '../../api';

const ensureUrlHasOnlyUnid = (unid: string) => {
  if (!isWeb) return;
  const url = new URL(window.location.href);
  // remove sensitive params
  url.searchParams.delete('id');
  url.searchParams.delete('token');
  // set/overwrite unid
  url.searchParams.set('unid', unid);
  const path =
    url.pathname +
    (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '');
  window.history.replaceState({}, '', path);
};

const useEhrLinkHandler = () => {
  const dispatch = useDispatch();
  const {currentUser, uniqueId} = useSelector((s: RootState) => s.auth);
  const didFetchRef = useRef(false);

  // On mount: capture id/token, persist, switch user if exists, and clean URL
  useEffect(() => {
    if (!isWeb) return;
    const params = new URLSearchParams(window.location.search);
    const idParam = params.get('id');
    const tokenParam = params.get('token');
    if (!idParam || !tokenParam) return;

    const users = getLocalData(keys.CURRENT_USER) || {};
    const existingUser = users[idParam] || {};

    // Store ehr_token on that user entry
    users[idParam] = {...existingUser, ehr_token: tokenParam};
    setLocalData(keys.CURRENT_USER, users);

    // Switch to that user if exists in storage (has app token)
    if (users[idParam] && (users[idParam].token || users[idParam].email)) {
      dispatch(login(users[idParam]));
    }
    dispatch(setUniqueId(idParam));

    // Clean URL and show only unid
    ensureUrlHasOnlyUnid(idParam);
  }, [dispatch]);

  // After login and when we have ehr_token, fetch EHR details and set selected case
  useEffect(() => {
    if (!isWeb) return;
    if (didFetchRef.current) return;
    if (!currentUser?.token) return;

    const users = getLocalData(keys.CURRENT_USER) || {};
    const activeKey = uniqueId || 'default';
    const ehrToken = users?.[activeKey]?.ehr_token;
    const ehrId = uniqueId || undefined; // EHR id equals unid as per spec
    if (!ehrToken || !ehrId) return;

    const fetchDetails = async () => {
      try {
        const res = await services.ehr.getEhrDetailsByIdToken(
          ehrId,
          encodeURIComponent(ehrToken),
        );
        // This endpoint requires app Authorization header (handled by axios interceptor)
        const text = res?.content?.patient_prompt || '';
        store.dispatch(setSelectedCase(text || ''));
        
        // Remove ehr_token from current user after successful fetch
        const updatedUsers = getLocalData(keys.CURRENT_USER) || {};
        if (updatedUsers[activeKey]) {
          const {ehr_token, ...userWithoutEhrToken} = updatedUsers[activeKey];
          updatedUsers[activeKey] = userWithoutEhrToken;
          setLocalData(keys.CURRENT_USER, updatedUsers);
        }
        
        didFetchRef.current = true;
      } catch (err) {
        // Silently ignore; toast is handled globally in http
      }
    };

    fetchDetails();
  }, [currentUser?.token, uniqueId]);
};

export default useEhrLinkHandler;
